/**
 * External Mode Controls Component
 * 
 * Displays provider/model selection controls when a thread is in External Mode.
 * These controls allow the user to select which provider/model to use for
 * messages in this thread, without changing their persisted default preferences.
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Sparkles, X } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface ChatProvider {
  provider: string;
  name: string;
  description: string;
  models: Array<{
    id: string;
    name: string;
    description: string;
    available: boolean;
  }>;
  available: boolean;
}

interface ExternalModeControlsProps {
  /** Current provider for this thread's external mode */
  provider: string;
  /** Current model for this thread's external mode */
  model: string;
  /** Called when provider changes */
  onProviderChange: (provider: string) => void;
  /** Called when model changes */
  onModelChange: (model: string) => void;
  /** Called when user wants to exit external mode */
  onExitExternalMode: () => void;
}

export function ExternalModeControls({
  provider,
  model,
  onProviderChange,
  onModelChange,
  onExitExternalMode,
}: ExternalModeControlsProps) {
  // Fetch available providers
  const { data: providers } = useQuery<ChatProvider[]>({
    queryKey: ['/api/chat/providers'],
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Get available models for selected provider
  const getAvailableModels = (providerKey: string) => {
    if (!providers) return [];
    const providerData = providers.find((p) => p.provider === providerKey);
    return providerData?.models?.filter(m => m.available) || [];
  };

  // Handle provider change - also update model to first available
  const handleProviderChange = (newProvider: string) => {
    onProviderChange(newProvider);
    const models = getAvailableModels(newProvider);
    if (models.length > 0 && !models.find(m => m.id === model)) {
      onModelChange(models[0].id);
    }
  };

  const availableModels = getAvailableModels(provider);
  const currentProviderData = providers?.find(p => p.provider === provider);
  const currentModelData = availableModels.find(m => m.id === model);

  return (
    <div className="flex items-center gap-2 px-2 py-1.5 bg-purple-50 border border-purple-200 rounded-lg text-sm">
      <div className="flex items-center gap-1.5 text-purple-700">
        <Sparkles className="w-3.5 h-3.5" />
        <span className="font-medium text-sm">External</span>
      </div>

      <div className="flex items-center gap-1.5">
        <Select value={provider} onValueChange={handleProviderChange}>
          <SelectTrigger className="h-7 w-[110px] text-sm bg-white border-purple-200">
            <SelectValue>
              {currentProviderData?.name || provider}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {providers?.filter(p => p.available).map((p) => (
              <SelectItem key={p.provider} value={p.provider}>
                {p.name || p.provider}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <span className="text-purple-400">/</span>

        <Select value={model} onValueChange={onModelChange}>
          <SelectTrigger className="h-7 w-[140px] text-sm bg-white border-purple-200">
            <SelectValue>
              {currentModelData?.name || model}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {availableModels.map((m) => (
              <SelectItem key={m.id} value={m.id}>
                <div className="flex flex-col">
                  <span>{m.name}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Button
        variant="ghost"
        size="sm"
        className="h-6 w-6 p-0 text-purple-600 hover:text-purple-800 hover:bg-purple-100"
        onClick={onExitExternalMode}
        title="Exit External Mode"
      >
        <X className="w-3.5 h-3.5" />
      </Button>
    </div>
  );
}

/**
 * External Mode Indicator - A compact badge shown in the thread header
 */
export function ExternalModeIndicator({
  provider,
  model,
}: {
  provider: string;
  model: string;
}) {
  return (
    <Badge 
      variant="outline" 
      className="text-xs bg-purple-50 text-purple-700 border-purple-200 flex items-center gap-1"
    >
      <Sparkles className="w-3 h-3" />
      External — {provider}/{model}
    </Badge>
  );
}

export default ExternalModeControls;

